/**
 * Centralized Application Configuration
 * All app-wide settings should be configured here for easy maintenance
 */

// Get runtime config with fallbacks
const getRuntimeConfig = () => {
  if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.SESSION) {
    return window.RUNTIME_CONFIG.SESSION;
  }
  return {};
};

const runtimeSession = getRuntimeConfig();

const appConfig = {
  // Session management configuration
  session: {
    // Rolling 7-day sessions: refresh token extends by 7 days with each use
    // Can be overridden via public/config.js or environment variable
    refreshTokenDays: runtimeSession.REFRESH_TOKEN_DAYS || 
                     parseInt(process.env.REACT_APP_REFRESH_TOKEN_DAYS) || 
                     7,

    // Days of inactivity before automatic logout (rolling sessions)
    // This should match the backend ROLLING_SESSION_IDLE_DAYS setting
    rollingSessionDays: runtimeSession.ROLLING_SESSION_DAYS || 
                        parseInt(process.env.REACT_APP_ROLLING_SESSION_DAYS) || 
                        7,

    // How many hours the access token is valid
    // Should match backend JWT_ACCESS_TOKEN_EXPIRE_MINUTES / 60
    accessTokenHours: runtimeSession.ACCESS_TOKEN_HOURS || 
                      parseInt(process.env.REACT_APP_ACCESS_TOKEN_HOURS) || 
                      168,  // 7 days

    // When to refresh token (0.8 = 80% of token lifetime)
    refreshThreshold: runtimeSession.REFRESH_THRESHOLD || 
                      parseFloat(process.env.REACT_APP_REFRESH_THRESHOLD) || 
                      0.8,

    // Retry configuration for token refresh
    maxRetries: 3,
    retryDelays: [1000, 2000, 5000], // Milliseconds between retries

    // Maximum retry attempts on temporary errors
    maxRetryOnError: 5,

    // How long to wait before retrying after an error
    errorRetryDelaySeconds: 30
  },

  // API configuration
  api: {
    baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
    timeout: 30000, // 30 seconds
    retryAttempts: 3
  },

  // Feature flags
  features: {
    enableTokenRefresh: true,
    enableCrossTabSync: true,
    enableOfflineMode: false,
    enableDebugLogging: process.env.NODE_ENV === 'development'
  },

  // File upload configuration
  upload: {
    // Maximum image size in MB (default: 20MB)
    // Can be overridden via environment variable
    maxImageSizeMB: parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 20,

    // Allowed image types
    allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'],

    // Allowed image extensions
    allowedImageExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],

    // Minimum image size in bytes (to avoid empty/malicious files)
    minImageSizeBytes: 100
  },

  // Helper methods to convert config values
  getRefreshTokenLifetimeMs() {
    return this.session.refreshTokenDays * 24 * 60 * 60 * 1000;
  },

  getAccessTokenLifetimeMs() {
    return this.session.accessTokenHours * 60 * 60 * 1000;
  },

  getTokenBufferMs() {
    // Buffer time before token expiry (5 minutes)
    return 5 * 60 * 1000;
  },

  getCheckIntervalMs() {
    // How often to check token status (1 minute)
    return 60 * 1000;
  },

  getErrorRetryDelayMs() {
    return this.session.errorRetryDelaySeconds * 1000;
  },

  // Helper method to get max image size in bytes
  getMaxImageSizeBytes() {
    return this.upload.maxImageSizeMB * 1024 * 1024;
  },

  // Helper method to get formatted max image size text
  getMaxImageSizeText() {
    return `${this.upload.maxImageSizeMB}MB`;
  }
};

export default appConfig;